import {
  SHARED_SPREADS,
  isSharedSpreadSlug,
  landingCardIdToAppImgSrc,
  type SharedSpreadSlug,
} from "./spreads";

/**
 * A reading the visitor prepared on the landing (question, spread, cards).
 * It travels to the app in the URL fragment (`#draft=…`), so the question
 * never reaches server logs, the Referer header or analytics.
 */

export const READING_DRAFT_HASH_KEY = "draft";
export const READING_DRAFT_VERSION = 1;
export const MAX_DRAFT_QUESTION_LENGTH = 1000;

export type ReadingDraftCard = {
  /** Landing card id, e.g. `major_0`, `cups_queen`. */
  id: string;
  /** Reversed. */
  r: boolean;
};

export type ReadingDraftMode = "own" | "drawn";

export type ReadingDraft = {
  v: typeof READING_DRAFT_VERSION;
  q: string;
  s: SharedSpreadSlug;
  c: ReadingDraftCard[];
  m: ReadingDraftMode;
  l: "ru" | "uk" | "en";
  /** Created at, ms since epoch. */
  t: number;
};

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

/** Checks shape, spread, card count, duplicates and question length. */
export function validateReadingDraft(raw: unknown): ReadingDraft | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }

  const o = raw as Record<string, unknown>;
  if (o.v !== READING_DRAFT_VERSION || !isSharedSpreadSlug(o.s)) {
    return null;
  }

  const question = typeof o.q === "string" ? o.q.trim() : "";
  if (!question || question.length > MAX_DRAFT_QUESTION_LENGTH) {
    return null;
  }

  if (!Array.isArray(o.c) || o.c.length !== SHARED_SPREADS[o.s].cardCount) {
    return null;
  }

  const seen = new Set<string>();
  const cards: ReadingDraftCard[] = [];
  for (const item of o.c) {
    if (typeof item !== "object" || item === null) {
      return null;
    }
    const card = item as Record<string, unknown>;
    if (
      typeof card.id !== "string" ||
      typeof card.r !== "boolean" ||
      !landingCardIdToAppImgSrc(card.id) ||
      seen.has(card.id)
    ) {
      return null;
    }
    seen.add(card.id);
    cards.push({ id: card.id, r: card.r });
  }

  const locale = o.l === "ru" || o.l === "uk" || o.l === "en" ? o.l : "uk";
  const createdAt =
    typeof o.t === "number" && Number.isFinite(o.t) ? o.t : Date.now();

  return {
    v: READING_DRAFT_VERSION,
    q: question,
    s: o.s,
    c: cards,
    m: o.m === "drawn" ? "drawn" : "own",
    l: locale,
    t: createdAt,
  };
}

export function encodeReadingDraft(draft: ReadingDraft): string {
  return toBase64Url(JSON.stringify(draft));
}

export function decodeReadingDraft(value: string): ReadingDraft | null {
  if (!value || value.length > 8000) {
    return null;
  }

  try {
    return validateReadingDraft(JSON.parse(fromBase64Url(value)));
  } catch {
    return null;
  }
}

/** Fragment for the app URL: `draft=<base64url>` (without the leading `#`). */
export function buildReadingDraftHash(draft: ReadingDraft): string {
  return `${READING_DRAFT_HASH_KEY}=${encodeReadingDraft(draft)}`;
}

/** Reads `#draft=…` from a location hash; other fragment params are ignored. */
export function parseReadingDraftHash(hash: string): ReadingDraft | null {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const value = params.get(READING_DRAFT_HASH_KEY);
  return value ? decodeReadingDraft(value) : null;
}
